import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { UserPlus, X, Check, Lock } from "lucide-react";
import { UserAvatar } from "../users/UserAvatar";

/**
 * Inline assignee picker with permission-aware behavior.
 *
 * Reassign rule (mirrored on the backend):
 *   Only (a) tenant Super Admin, (b) task creator (created_by),
 *   or (c) the current assignee may (re)assign or unassign a task.
 *
 * When `canReassign` is false, the trigger renders as read-only text with
 * a tooltip explaining the rule — no popover, no clicks. When true, the
 * picker also asks the caller for explicit confirmation whenever the
 * selection would transfer ownership away from the current user.
 */
export default function InlineAssigneePicker({
  assigneeId,
  users,
  onSelect,
  disabled = false,
  testId,
  canReassign = true,
  currentUserId,
  taskTitle,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirmNew, setConfirmNew] = useState(null); // { id, name } | null

  const current = users.find((u) => u.id === assigneeId);
  const active = users.filter((u) => u.active !== false);

  // If the caller is the current assignee and NOT super_admin / creator,
  // assigning to someone else means they lose reassign rights afterwards.
  // We surface this with an AlertDialog to prevent surprise.
  const willLoseRights = (targetId) =>
    currentUserId &&
    assigneeId === currentUserId &&
    targetId !== currentUserId;

  const commit = (targetId) => {
    setOpen(false);
    onSelect(targetId);
  };

  const requestSelect = (targetId, targetName) => {
    if (willLoseRights(targetId)) {
      setConfirmNew({ id: targetId, name: targetName || t("tasks.unassigned") });
      setOpen(false);
      return;
    }
    commit(targetId);
  };

  const label = current ? (
    <span className="inline-flex items-center gap-1.5">
      <UserAvatar
        name={current.name}
        email={current.email}
        photoUrl={current.photo_url}
        size="xs"
      />
      <span className="text-xs text-zinc-700">{current.name}</span>
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-700">
      <UserPlus className="h-3 w-3" />
      {t("inline.assign")}
    </span>
  );

  // Locked (read-only) mode — no popover, tooltip explains why
  if (!canReassign) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid={testId}
              className="inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-xs text-zinc-500 cursor-default select-none"
            >
              {current ? (
                <>
                  <UserAvatar
                    name={current.name}
                    email={current.email}
                    photoUrl={current.photo_url}
                    size="xs"
                  />
                  <span>{current.name}</span>
                </>
              ) : (
                t("tasks.unassigned")
              )}
              <Lock className="h-2.5 w-2.5 text-zinc-400" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px] text-xs">
            {t("tasks.reassign_locked_hint")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <>
      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testId}
            onClick={(e) => e.stopPropagation()}
            className="rounded-md px-1 py-0.5 hover:bg-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors"
          >
            {label}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-64 p-0"
          align="end"
          onClick={(e) => e.stopPropagation()}
        >
          <Command shouldFilter={true}>
            <CommandInput placeholder={t("inline.search_user")} />
            <CommandList>
              <CommandEmpty>{t("inline.no_users")}</CommandEmpty>
              <CommandGroup>
                {assigneeId && (
                  <CommandItem
                    onSelect={() => requestSelect(null, null)}
                    data-testid="inline-assignee-unassign"
                    className="text-red-600 aria-selected:bg-red-50"
                  >
                    <X className="mr-2 h-3.5 w-3.5" />
                    {t("inline.unassign")}
                  </CommandItem>
                )}
                {active.map((u) => (
                  <CommandItem
                    key={u.id}
                    onSelect={() => requestSelect(u.id, u.name)}
                    data-testid={`inline-assignee-pick-${u.id}`}
                  >
                    <div className="mr-2">
                      <UserAvatar
                        name={u.name}
                        email={u.email}
                        photoUrl={u.photo_url}
                        size="xs"
                      />
                    </div>
                    <span className="flex-1 text-sm">{u.name}</span>
                    {u.id === assigneeId && <Check className="h-3.5 w-3.5" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <AlertDialog
        open={!!confirmNew}
        onOpenChange={(v) => !v && setConfirmNew(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tasks.reassign_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("tasks.reassign_confirm_body", {
                name: confirmNew?.name || "",
                task: taskTitle || "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reassign-cancel-btn">
              {t("common.no")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reassign-confirm-btn"
              onClick={() => {
                if (confirmNew) commit(confirmNew.id);
                setConfirmNew(null);
              }}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
            >
              {t("common.yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
